<template>
  <div
    class="grid gap-5 text-left md:grid-cols-[minmax(0,1fr)_340px] md:items-start"
    data-testid="send-record-step-4"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
    @pointerdown="markTypingFromInteraction"
    @keydown="markTypingFromInteraction"
    @input="markTypingFromInteraction"
  >
    <!--
      Celular (⑨–⑫): o anexo vem PRIMEIRO e fica preso ao topo enquanto o
      formulário rola por baixo. Mesmo conteúdo do desktop, só em outra ordem.
      É um `v-if` e não um `md:hidden`: com os dois painéis montados, cada anexo
      seria carregado duas vezes.
    -->
    <div
      v-if="!isDesktop"
      ref="mobilePanel"
      class="sticky top-0 z-10 -mx-1 bg-white px-1 pb-2 pt-1"
    >
      <AttachmentPanel
        layout="carousel"
        :files="files"
        :compact="isTyping"
        :tolerance-hint="toleranceHint"
        :active-index="activeIndex"
        @update:active-index="emit('update:activeIndex', $event)"
      />
    </div>

    <div>
      <p class="text-[15px] font-bold text-textPrimary">
        Declare o horário que este anexo comprova
      </p>
      <p class="mt-0.5 text-[12.5px] text-neutral50">
        Começamos com o horário previsto do plantão. <strong>Digite por cima</strong> para informar
        o horário real que aparece no anexo.
      </p>

      <div
        v-for="line in lines"
        :key="line.doctorStationId"
        class="mt-3 rounded-[10px] border bg-white px-4 py-3.5"
        :class="
          line.doctorStationId === activeLineId
            ? 'border-primary10 ring-[3px] ring-primary5'
            : 'border-lightGray30'
        "
        :data-testid="`send-record-line-${line.doctorStationId}`"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <p class="min-w-0 text-[13px] text-neutral50">
            <span class="text-sm font-bold text-textPrimary">{{ headOf(line) }}</span>
            {{ whenOf(line) }}
          </p>
          <span
            class="inline-flex flex-none items-center gap-1.5 rounded-full px-2 py-[3px] text-[11.5px] font-bold"
            :class="stateOf(line).classes"
          >
            {{ stateOf(line).label }}
          </span>
        </div>

        <!--
          Balão ③: as três pernas viram cartões de rádio. O select escondia que
          existem três opções e o que cada uma libera; o par de barrinhas mostra
          quais campos de hora ficam ativos.
        -->
        <div
          class="mt-2.5 grid gap-2 sm:grid-cols-3"
          role="radiogroup"
          :aria-label="`Este registro equivale a — plantão ${line.doctorStationId}`"
        >
          <label
            v-for="option in LEG_OPTIONS"
            :key="option.value"
            class="relative flex cursor-pointer items-start gap-2.5 rounded-[10px] border-[1.5px] px-3 py-2.5"
            :class="
              line.coveredLegs === option.value
                ? 'border-primary30 bg-primary5'
                : 'border-lightGray30 bg-white'
            "
          >
            <input
              type="radio"
              class="absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0"
              :name="`leg-${line.doctorStationId}`"
              :value="option.value"
              :checked="line.coveredLegs === option.value"
              :aria-describedby="`leg-help-${line.doctorStationId}-${option.value}`"
              :data-testid="`send-record-leg-${line.doctorStationId}-${option.value}`"
              @change="updateLeg(line.doctorStationId, option.value)"
            />
            <span
              class="mt-0.5 flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border-2"
              :class="line.coveredLegs === option.value ? 'border-primary30' : 'border-neutral50'"
            >
              <span
                v-if="line.coveredLegs === option.value"
                class="h-[9px] w-[9px] rounded-full bg-primary30"
              />
            </span>
            <span class="min-w-0">
              <b class="block text-[13.5px] text-textPrimary">{{ option.label }}</b>
              <small
                :id="`leg-help-${line.doctorStationId}-${option.value}`"
                class="mt-0.5 block text-[11.5px] leading-tight text-neutral50"
              >
                {{ option.help }}
              </small>
              <span class="mt-1.5 inline-flex gap-[3px]">
                <i
                  class="h-1.5 w-[22px] rounded-[3px]"
                  :class="option.bars[0] ? 'bg-primary30' : 'bg-lightGray30'"
                />
                <i
                  class="h-1.5 w-[22px] rounded-[3px]"
                  :class="option.bars[1] ? 'bg-primary30' : 'bg-lightGray30'"
                />
              </span>
            </span>
          </label>
        </div>

        <!--
          Balões ① e ②: campo grande, com borda e ícone. O previsto vira REFERÊNCIA
          no rótulo, e não um valor "pronto" que ninguém percebe ser editável.
        -->
        <div class="mt-3.5 grid gap-3 sm:grid-cols-2">
          <div v-for="leg in TIME_LEGS" :key="leg.field">
            <label
              class="mb-1.5 flex items-center justify-between text-[12.5px] font-bold text-neutral50"
              :for="`${leg.testidPrefix}-${line.doctorStationId}`"
            >
              {{ leg.label }}
              <span class="font-normal text-neutral50">
                previsto {{ expectedOf(line, leg.field) }}
              </span>
            </label>
            <div
              class="flex h-[46px] items-center gap-2 rounded-lg border-[1.5px] px-3 focus-within:border-primary30 focus-within:ring-4 focus-within:ring-primary30/30"
              :class="
                covers(line, leg.covered)
                  ? 'border-neutral50 bg-white text-textPrimary'
                  : 'border-dashed border-lightGray30 bg-gray-50 text-neutral50'
              "
            >
              <v-icon
                :icon="leg.covered === CoveredLegs.CHECKIN ? mdiLoginVariant : mdiLogoutVariant"
                size="18"
                class="flex-none"
              />
              <input
                :id="`${leg.testidPrefix}-${line.doctorStationId}`"
                :ref="(el) => setTimeInput(`${leg.field}-${line.doctorStationId}`, el)"
                type="time"
                class="w-full min-w-0 border-0 bg-transparent p-0 text-[22px] font-bold tabular-nums tracking-wide text-inherit outline-none"
                :value="toTimeInput(line[leg.field], line.timezone)"
                :disabled="!covers(line, leg.covered)"
                :aria-describedby="`hint-${leg.field}-${line.doctorStationId}`"
                :data-testid="`${leg.testidPrefix}-${line.doctorStationId}`"
                @input="updateTime(line.doctorStationId, leg.field, $event)"
              />
              <span class="flex-none text-xs text-neutral50">UTC{{ line.timezone }}</span>
            </div>

            <!--
              Balão ⑳: o aviso de tolerância NUNCA bloqueia — ele existe para o emissor
              corrigir um erro de digitação antes de gerar trabalho na fila. Por isso ele
              SUBSTITUI a dica da perna: duas linhas competindo pelo mesmo olhar viram ruído.
            -->
            <p
              v-if="warningFor(line, leg.covered)"
              :id="`hint-${leg.field}-${line.doctorStationId}`"
              class="mt-1.5 text-[11.5px] text-[#8A6A00]"
              :data-testid="`send-record-tolerance-warning-${line.doctorStationId}`"
            >
              {{ warningFor(line, leg.covered)?.text }}. O registro segue normalmente e entra na
              fila marcado como divergente para o auditor conferir.
            </p>
            <p
              v-else-if="!covers(line, leg.covered)"
              :id="`hint-${leg.field}-${line.doctorStationId}`"
              class="mt-1.5 text-[11.5px] italic text-neutral50"
            >
              Não coberto por este anexo. Fica como o sistema tem.
            </p>
            <!--
              Perna coberta e campo vazio: é isto que segura o "Próximo". Sem a linha
              abaixo o botão ficava cinza e a pessoa não tinha como saber o motivo.
            -->
            <p
              v-else-if="isLegTimeMissing(line, leg.covered)"
              :id="`hint-${leg.field}-${line.doctorStationId}`"
              class="mt-1.5 text-[11.5px] font-bold text-[#8A6A00]"
              :data-testid="`send-record-missing-time-${line.doctorStationId}-${leg.covered}`"
            >
              Informe o horário para continuar
            </p>
            <p
              v-else-if="leg.covered === CoveredLegs.CHECKIN"
              :id="`hint-${leg.field}-${line.doctorStationId}`"
              class="mt-1.5 text-[11.5px] text-sucess"
            >
              ✓ Confira com o anexo <span class="md:hidden">acima</span>
              <span class="hidden md:inline">ao lado</span> · [Tab] vai para o check-out
            </p>
            <p
              v-else
              :id="`hint-${leg.field}-${line.doctorStationId}`"
              class="mt-1.5 text-[11.5px] italic text-neutral50"
            >
              Deixe {{ expectedOf(line, leg.field) }} se o anexo confirmar a saída no horário.
            </p>
          </div>
        </div>

        <!--
          Balão ⑥: o fuso é uma LINHA DE TEXTO com "Alterar". O seletor sai do caminho de
          quem está em Brasília e nasce aberto quando a escala já está fora dela — ou
          quando esta linha foi declarada em outro fuso. Ele continua no DOM (`v-show`)
          porque é o mesmo controle de sempre, só recolhido.
        -->
        <p
          class="mt-2.5 flex flex-wrap items-center gap-1.5 text-xs text-neutral50"
          :data-testid="`send-record-timezone-hint-${line.doctorStationId}`"
        >
          Horários no fuso da escala ({{ zoneTitle(line.timezone) }}, UTC{{ line.timezone }}){{
            timezoneOrigin(line)
          }}.
          <button
            v-if="!isZoneOpen(line)"
            type="button"
            class="font-bold text-primary30 underline"
            :data-testid="`send-record-timezone-toggle-${line.doctorStationId}`"
            @click="openZone(line.doctorStationId)"
          >
            Plantão em outro fuso? Alterar
          </button>
        </p>
        <label
          v-show="isZoneOpen(line)"
          class="mt-1.5 flex flex-col text-xs text-neutral50"
          :for="`send-record-timezone-${line.doctorStationId}`"
        >
          Fuso do plantão
          <select
            :id="`send-record-timezone-${line.doctorStationId}`"
            class="mt-0.5 w-fit rounded border border-gray-200 px-2 py-1 text-sm text-textPrimary"
            :value="line.timezone"
            :data-testid="`send-record-timezone-${line.doctorStationId}`"
            @change="updateTimezone(line.doctorStationId, $event)"
          >
            <option v-for="zone in timezoneOptions" :key="zone.value" :value="zone.value">
              {{ zone.title }} (UTC{{ zone.value }})
            </option>
          </select>
        </label>

        <!--
          Balões ⑦ e ⑲: "o que o sistema já tem" desce para o rodapé do cartão, em letra
          menor. Continua explicando o chip do cabeçalho — horário, fonte e estado POR
          PERNA, o dado da F1 que justifica a perna sugerida — sem competir com o que a
          pessoa precisa fazer.
        -->
        <div
          class="mt-3 border-t border-dashed border-lightGray30 pt-2.5 text-[12.5px] text-neutral50"
          data-testid="send-record-system-has"
        >
          <p>O sistema tem</p>
          <div
            v-for="systemLeg in systemLegsOf(line)"
            :key="systemLeg.key"
            class="mt-0.5 flex items-baseline gap-2"
          >
            <span class="w-[76px] flex-none">{{ systemLeg.label }}</span>
            <b class="w-11 flex-none tabular-nums text-textPrimary">{{ systemLeg.time || '—' }}</b>
            <span class="flex-1">{{ systemLeg.detail }}</span>
            <span class="flex-none font-bold">{{ systemLeg.status }}</span>
          </div>
        </div>
      </div>

      <p class="mt-3 text-xs text-neutral50">
        Guardamos o horário declarado, quem declarou e a data. Se o auditor sobrescrever na
        auditoria, o valor declarado continua visível no histórico ao lado do valor final.
      </p>
    </div>

    <AttachmentPanel
      v-if="isDesktop"
      layout="side"
      :files="files"
      :tolerance-hint="toleranceHint"
      :active-index="activeIndex"
      @update:active-index="emit('update:activeIndex', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { mdiLoginVariant, mdiLogoutVariant } from '@mdi/js'
import {
  CoveredLegs,
  EffectiveRegistrationStatus,
  EFFECTIVE_REGISTRATION_STATUS_LABELS,
  EFFECTIVE_SOURCE_KIND_LABELS
} from '@/constants/ds-attendance.constants'
import { timezones } from '@/hooks/useCommon'
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { DEFAULT_UTC_OFFSET, formatDateInOffset } from '@/utils/date'
import AttachmentPanel from './AttachmentPanel.vue'
import {
  applyTimeInputToIso,
  buildToleranceWarnings,
  isLegTimeMissing,
  isoToTimeInput,
  type NormalizedPrefillItem,
  type SendRecordLine,
  type ToleranceWarning
} from './types'

/**
 * Passo 4 de M2 — o coração do fluxo. O emissor declara O QUE ESTE REGISTRO
 * SUBSTITUI (check-in, check-out ou ambos) e com que horário. Pré-preenchido com
 * o PREVISTO do plantão (Q5) e não com o efetivo: pré-preencher com o efetivo
 * faria o emissor repetir o que já existe.
 *
 * M6 (17/09) reformou a tela depois do primeiro uso em PROD: os horários pareciam
 * texto, o foco não chegava em campo nenhum, o anexo tinha ficado na etapa 3 e as
 * três pernas estavam escondidas num select.
 */

const props = withDefaults(
  defineProps<{
    prefill: NormalizedPrefillItem[]
    lines: SendRecordLine[]
    /** Tolerâncias da política quando o prefill não as traz. */
    checkinLateToleranceMinutes: number | null
    checkoutEarlyToleranceMinutes: number | null
    /** Anexos da etapa 3, para a conferência lado a lado (④). */
    files?: File[]
    /** Anexo em exibição — compartilhado com as etapas 3 e 5 pelo modal. */
    activeIndex?: number | null
  }>(),
  { files: () => [], activeIndex: null }
)

/**
 * Não há `update:typing`: quem monta o painel do anexo é ESTE passo, então o
 * estado de digitação (que vira o recorte do ⑫ no celular) nasce e morre aqui.
 * Exportá-lo criaria uma cópia no modal que ninguém leria.
 */
const emit = defineEmits<{
  'update:lines': [value: SendRecordLine[]]
  'update:activeIndex': [value: number]
}>()

const LEG_OPTIONS: Array<{
  value: CoveredLegs
  label: string
  help: string
  bars: [boolean, boolean]
}> = [
  {
    value: CoveredLegs.BOTH,
    label: 'Entrada e saída',
    help: 'O anexo comprova o plantão inteiro. Libera os dois horários.',
    bars: [true, true]
  },
  {
    value: CoveredLegs.CHECKIN,
    label: 'Só a entrada',
    help: 'Só o check-in. A saída continua como o sistema tem hoje.',
    bars: [true, false]
  },
  {
    value: CoveredLegs.CHECKOUT,
    label: 'Só a saída',
    help: 'Só o check-out. A entrada continua como o sistema tem hoje.',
    bars: [false, true]
  }
]

type TimeField = 'declaredCheckin' | 'declaredCheckout'

const TIME_LEGS = [
  {
    field: 'declaredCheckin' as const,
    covered: CoveredLegs.CHECKIN,
    label: 'Check-in',
    testidPrefix: 'send-record-checkin'
  },
  {
    field: 'declaredCheckout' as const,
    covered: CoveredLegs.CHECKOUT,
    label: 'Check-out',
    testidPrefix: 'send-record-checkout'
  }
]

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

const prefillById = computed(
  () => new Map(props.prefill.map((item) => [item.doctorStationId, item]))
)

function itemOf(line: SendRecordLine): NormalizedPrefillItem | undefined {
  return prefillById.value.get(line.doctorStationId)
}

function covers(line: SendRecordLine, leg: CoveredLegs): boolean {
  return line.coveredLegs === leg || line.coveredLegs === CoveredLegs.BOTH
}

function toTimeInput(value: string | null, offset: string): string {
  return isoToTimeInput(value, offset)
}

/* ----------------------------------------------------------------------- *
 * Cabeçalho do cartão
 * ----------------------------------------------------------------------- */

function headOf(line: SendRecordLine): string {
  return itemOf(line)?.doctorName ?? `Plantão #${line.doctorStationId}`
}

/** `· Qui 17/09/2026 · previsto 07:00–13:00 · Enfermaria 1` */
function whenOf(line: SendRecordLine): string {
  const item = itemOf(line)
  if (!item) return ''
  const day = formatDayWithWeekday(item.expectedStart, item.timezone)
  const expected =
    item.expectedStart && item.expectedEnd
      ? `previsto ${isoToTimeInput(item.expectedStart, item.timezone)}–${isoToTimeInput(item.expectedEnd, item.timezone)}`
      : ''
  return [day, expected, item.healthUnitName]
    .filter(Boolean)
    .map((part) => `· ${part}`)
    .join(' ')
}

/**
 * Dia lido NO FUSO DA ESCALA — nunca no do navegador, a mesma regra dos horários.
 * O `Date.UTC` sobre a data já deslocada devolve o dia da semana sem reintroduzir
 * o fuso da máquina.
 */
function formatDayWithWeekday(iso: string | null, offset: string): string {
  if (!iso) return ''
  const formatted = formatDateInOffset(iso, offset)
  if (!formatted) return ''
  const [day, month, year] = formatted.split('/').map(Number)
  const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()] ?? ''
  return `${weekday} ${formatted}`.trim()
}

/** Chip do cabeçalho: o estado efetivo do plantão, resumido (M1, balão ①). */
function stateOf(line: SendRecordLine): { label: string; classes: string } {
  const effective = itemOf(line)?.effective
  const neutral = 'bg-gray-100 text-neutral50'
  if (!effective) {
    return {
      label: EFFECTIVE_REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.PENDING],
      classes: neutral
    }
  }

  switch (effective.registrationStatus) {
    case EffectiveRegistrationStatus.OK:
      return { label: 'Registro OK', classes: 'bg-green-50 text-sucess' }
    case EffectiveRegistrationStatus.PARTIAL:
      return {
        label: effective.checkout ? 'Falta só o check-in' : 'Falta só o check-out',
        classes: 'bg-orange-50 text-[#B45F09]'
      }
    case EffectiveRegistrationStatus.PENDING:
      return { label: 'Pendente', classes: neutral }
    default:
      return { label: 'Sem registro nas duas pernas', classes: 'bg-red-50 text-negative' }
  }
}

/* ----------------------------------------------------------------------- *
 * Horários
 * ----------------------------------------------------------------------- */

function expectedOf(line: SendRecordLine, field: 'declaredCheckin' | 'declaredCheckout'): string {
  const item = itemOf(line)
  const expected = field === 'declaredCheckin' ? item?.expectedStart : item?.expectedEnd
  return isoToTimeInput(expected ?? null, line.timezone) || '—'
}

function patch(doctorStationId: number, patchValue: Partial<SendRecordLine>): void {
  emit(
    'update:lines',
    props.lines.map((line) =>
      line.doctorStationId === doctorStationId ? { ...line, ...patchValue } : line
    )
  )
}

/** Horário zerado ao escolher uma perna única — a rede de segurança do "voltar a ambos". */
const clearedTimes = new Map<number, Partial<Record<TimeField, string | null>>>()

function remember(doctorStationId: number, field: TimeField, value: string | null): void {
  if (!value) return
  clearedTimes.set(doctorStationId, { ...clearedTimes.get(doctorStationId), [field]: value })
}

/**
 * De volta às duas pernas, a que tinha sido zerada volta ao PREVISTO (Q5) — nunca
 * ao efetivo, que faria o emissor repetir o que o sistema já tem. Prefill sem
 * previsto (backend antigo, plantão sem horário) cai no que a linha tinha ANTES de
 * ser zerada: pior que um horário para conferir é um campo vazio que trava o
 * "Próximo" sem dizer por quê.
 */
function restoreTime(line: SendRecordLine, field: TimeField): string | null {
  const item = prefillById.value.get(line.doctorStationId)
  const expected = field === 'declaredCheckin' ? item?.expectedStart : item?.expectedEnd
  return expected ?? clearedTimes.get(line.doctorStationId)?.[field] ?? null
}

/** Trocar a perna leva o foco para o campo que acabou de ficar ativo (③ + Q2). */
function updateLeg(doctorStationId: number, coveredLegs: CoveredLegs): void {
  const line = props.lines.find((item) => item.doctorStationId === doctorStationId)
  const patchValue: Partial<SendRecordLine> = { coveredLegs }

  if (coveredLegs === CoveredLegs.BOTH) {
    if (line && !line.declaredCheckin)
      patchValue.declaredCheckin = restoreTime(line, 'declaredCheckin')
    if (line && !line.declaredCheckout)
      patchValue.declaredCheckout = restoreTime(line, 'declaredCheckout')
  } else {
    // O anexo comprova UMA perna: deixar o horário da outra na tela (mesmo cinza e
    // desabilitado) fazia a pessoa achar que ela também estava sendo declarada.
    // Zerado, o campo mostra o "--:--" do próprio navegador e a dica explica o resto.
    const dropped: TimeField =
      coveredLegs === CoveredLegs.CHECKIN ? 'declaredCheckout' : 'declaredCheckin'
    remember(doctorStationId, dropped, line?.[dropped] ?? null)
    patchValue[dropped] = null
  }

  patch(doctorStationId, patchValue)
  focusLeg(doctorStationId, coveredLegs)
}

function updateTime(
  doctorStationId: number,
  field: 'declaredCheckin' | 'declaredCheckout',
  event: Event
): void {
  const item = prefillById.value.get(doctorStationId)
  const base = field === 'declaredCheckin' ? item?.expectedStart : item?.expectedEnd
  const current = props.lines.find((line) => line.doctorStationId === doctorStationId)
  const reference = base ?? current?.[field] ?? null
  patch(doctorStationId, {
    [field]: applyTimeInputToIso(
      reference,
      (event.target as HTMLInputElement).value,
      current?.timezone
    )
  })
}

function warningsOf(line: SendRecordLine): ToleranceWarning[] {
  const item = itemOf(line)
  return buildToleranceWarnings({
    line,
    expectedStart: item?.expectedStart ?? null,
    expectedEnd: item?.expectedEnd ?? null,
    checkinLateToleranceMinutes:
      item?.checkinLateToleranceMinutes ?? props.checkinLateToleranceMinutes,
    checkoutEarlyToleranceMinutes:
      item?.checkoutEarlyToleranceMinutes ?? props.checkoutEarlyToleranceMinutes
  })
}

function warningFor(line: SendRecordLine, leg: CoveredLegs): ToleranceWarning | undefined {
  if (!covers(line, leg)) return undefined
  return warningsOf(line).find((warning) => warning.leg === leg)
}

/* ----------------------------------------------------------------------- *
 * Fuso
 * ----------------------------------------------------------------------- */

/**
 * Lista de fusos do cadastro de escala (`useCommon`), para o seletor não inventar
 * um vocabulário próprio. Um fuso vindo da escala que não esteja na lista entra
 * como opção extra: nunca some do seletor o valor que está sendo enviado.
 */
const timezoneOptions = computed(() => {
  const known = new Set(timezones.map((zone) => zone.value))
  const extras = props.lines
    .map((line) => line.timezone)
    .filter((value): value is string => Boolean(value) && !known.has(value))
    .map((value) => ({ title: 'Fuso da escala', subtitle: value, value }))
  return [...timezones, ...extras]
})

function zoneTitle(offset: string): string {
  return timezones.find((zone) => zone.value === offset)?.title ?? 'fuso da escala'
}

/** Diz se o fuso mostrado ainda é o que veio da escala ou um escolhido à mão. */
function timezoneOrigin(line: SendRecordLine): string {
  const scheduleTimezone = itemOf(line)?.timezone
  return !scheduleTimezone || scheduleTimezone === line.timezone
    ? ''
    : ` — a escala é UTC${scheduleTimezone}`
}

const openedZones = ref<number[]>([])

/**
 * O seletor nasce ABERTO quando o fuso já não é o óbvio: a linha foi declarada
 * em outro fuso, ou a própria escala está fora de Brasília. Nos demais casos ele
 * só aparece depois do "Alterar" (Q4).
 */
function isZoneOpen(line: SendRecordLine): boolean {
  if (openedZones.value.includes(line.doctorStationId)) return true
  const scheduleTimezone = itemOf(line)?.timezone
  if (scheduleTimezone && scheduleTimezone !== line.timezone) return true
  return (scheduleTimezone ?? line.timezone) !== DEFAULT_UTC_OFFSET
}

function openZone(doctorStationId: number): void {
  if (!openedZones.value.includes(doctorStationId)) openedZones.value.push(doctorStationId)
}

/**
 * Trocar o fuso preserva os DÍGITOS digitados e troca só o offset: quem viu 08:00
 * na tela declarou 08:00 na unidade, e o que muda é em que fuso esse 08:00 vale.
 */
function updateTimezone(doctorStationId: number, event: Event): void {
  const timezone = (event.target as HTMLSelectElement).value
  const line = props.lines.find((item) => item.doctorStationId === doctorStationId)
  if (!line) return

  const checkin = isoToTimeInput(line.declaredCheckin, line.timezone)
  const checkout = isoToTimeInput(line.declaredCheckout, line.timezone)

  patch(doctorStationId, {
    timezone,
    declaredCheckin: checkin
      ? applyTimeInputToIso(line.declaredCheckin, checkin, timezone)
      : line.declaredCheckin,
    declaredCheckout: checkout
      ? applyTimeInputToIso(line.declaredCheckout, checkout, timezone)
      : line.declaredCheckout
  })
}

/* ----------------------------------------------------------------------- *
 * "O sistema tem"
 * ----------------------------------------------------------------------- */

function systemLegsOf(line: SendRecordLine) {
  const item = itemOf(line)
  if (!item) return []
  return [
    {
      key: 'checkin',
      label: 'Check-in',
      time: isoToTimeInput(item.effective.checkin, item.timezone),
      detail: detail(item.effective.checkinSource, item.expectedStart, item.timezone),
      status: legStatus(item.effective.checkin)
    },
    {
      key: 'checkout',
      label: 'Check-out',
      time: isoToTimeInput(item.effective.checkout, item.timezone),
      detail: detail(item.effective.checkoutSource, item.expectedEnd, item.timezone),
      status: legStatus(item.effective.checkout)
    }
  ]
}

function detail(
  source: keyof typeof EFFECTIVE_SOURCE_KIND_LABELS | null,
  expected: string | null,
  offset: string
): string {
  const sourceLabel = source ? EFFECTIVE_SOURCE_KIND_LABELS[source] : 'Nenhuma fonte'
  const expectedLabel = expected ? ` · previsto ${isoToTimeInput(expected, offset)}` : ''
  return `${sourceLabel}${expectedLabel}`
}

function legStatus(value: string | null): string {
  return value
    ? EFFECTIVE_REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.OK]
    : EFFECTIVE_REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.NONE]
}

/* ----------------------------------------------------------------------- *
 * Dica de tolerância do painel do anexo (⑤)
 * ----------------------------------------------------------------------- */

const toleranceHint = computed(() => {
  const candidates = [
    props.checkinLateToleranceMinutes,
    props.checkoutEarlyToleranceMinutes,
    ...props.prefill.flatMap((item) => [
      item.checkinLateToleranceMinutes,
      item.checkoutEarlyToleranceMinutes
    ])
  ].filter((value): value is number => typeof value === 'number')

  const window = candidates.length
    ? `Diferenças até ${Math.max(...candidates)} min entram como “dentro da tolerância”`
    : 'Diferenças pequenas entram como “dentro da tolerância”'

  return (
    `Compare o horário do anexo com o que você digitou. ${window}; ` +
    'acima disso o registro segue, marcado como divergente para a auditoria.'
  )
})

/* ----------------------------------------------------------------------- *
 * Foco e teclado (balões ① e ⑧ / Q2)
 * ----------------------------------------------------------------------- */

const timeInputs = new Map<string, HTMLInputElement>()
const activeLineId = ref<number | null>(props.lines[0]?.doctorStationId ?? null)
const isTyping = ref(false)
/** Painel preso ao topo no celular — é a altura dele que esconderia o campo. */
const mobilePanel = ref<HTMLElement | null>(null)

/** Só o painel do layout atual é montado — ver o comentário do `v-if` no template. */
const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY)

function setTimeInput(key: string, element: unknown): void {
  if (element instanceof HTMLInputElement) timeInputs.set(key, element)
  else timeInputs.delete(key)
}

/**
 * O `focusin` de um `.focus()` do próprio código é indistinguível do de um toque
 * (`isTrusted` é `true` nos dois), então a marca vem daqui: enquanto este sinal
 * está ligado, o foco é NOSSO e não conta como digitação.
 */
let programmaticFocus = false

/**
 * No celular o carrossel é `sticky` no topo do corpo do modal e ocupa ~330 px
 * (~260 px no recorte). Sem isto, focar o check-in deixava o campo EMBAIXO do
 * painel: aparecia só a dica "Confira com o anexo acima", e a pessoa digitava
 * às cegas. Duas defesas complementares:
 *
 * - `scroll-margin-top` do tamanho do painel + 12 px, medido de verdade, para o
 *   Tab entre os campos (rolagem do próprio navegador) já parar abaixo dele;
 * - um `scrollIntoView({ block: 'center' })` depois do foco, que resolve o caso
 *   em que o campo já estava na tela mas atrás do painel.
 *
 * O elemento que rola é o `<main>` do `Modal.vue` (`overflow-y-auto`); como
 * `scrollIntoView` sobe sozinho por todos os ancestrais roláveis, ele serve
 * tanto para o corpo do modal quanto para a janela, quando o modal não rola.
 */
function keepInputVisible(input: HTMLInputElement): void {
  if (isDesktop.value) return

  const panelHeight = mobilePanel.value?.getBoundingClientRect().height ?? 0
  if (panelHeight) input.style.scrollMarginTop = `${Math.round(panelHeight) + 12}px`

  if (typeof input.scrollIntoView !== 'function') return
  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  input.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' })
}

function focusLeg(doctorStationId: number, coveredLegs: CoveredLegs): void {
  const field = coveredLegs === CoveredLegs.CHECKOUT ? 'declaredCheckout' : 'declaredCheckin'
  void nextTick(() => {
    const input = timeInputs.get(`${field}-${doctorStationId}`)
    programmaticFocus = true
    try {
      input?.focus()
    } finally {
      programmaticFocus = false
    }
    if (input) void nextTick(() => keepInputVisible(input))
  })
}

onMounted(() => {
  const first = props.lines[0]
  if (first) focusLeg(first.doctorStationId, first.coveredLegs)
})

function timeInputOf(target: EventTarget | null): HTMLInputElement | null {
  return target instanceof HTMLInputElement && target.type === 'time' ? target : null
}

function setTyping(value: boolean): void {
  if (isTyping.value === value) return
  isTyping.value = value
}

/**
 * O foco automático do `onMounted` não é digitação: ele acontece ANTES de a
 * pessoa tocar na tela, e tratá-lo como tal abriria a etapa já no recorte do
 * horário, escondendo o anexo inteiro que ela ainda não viu. O recorte só liga em
 * interação de verdade — um toque, uma tecla ou o próprio texto digitado. O
 * cartão em destaque, esse sim, acompanha qualquer foco.
 */
function handleFocusIn(event: FocusEvent): void {
  const input = timeInputOf(event.target)
  if (!input) return
  const line = props.lines.find((item) => input.id.endsWith(`-${item.doctorStationId}`))
  if (line) activeLineId.value = line.doctorStationId
  if (!programmaticFocus) setTyping(true)
  // Depois do `nextTick` o painel já mudou de altura (inteiro ↔ recorte), então a
  // medida usada aqui é a que vale quando a rolagem acontece.
  void nextTick(() => keepInputVisible(input))
}

/**
 * A troca inteiro ↔ recorte muda a altura do painel: o campo em foco precisa ser
 * recentrado com a medida nova, senão ele reaparece atrás do carrossel.
 */
watch(isTyping, () => {
  const input = timeInputOf(document.activeElement)
  if (!input) return
  void nextTick(() => keepInputVisible(input))
})

function markTypingFromInteraction(event: Event): void {
  if (!timeInputOf(event.target)) return
  setTyping(true)
}

function handleFocusOut(event: FocusEvent): void {
  if (!timeInputOf(event.target)) return
  // Tab de um horário para o outro não fecha o recorte: o teclado continua aberto.
  if (timeInputOf(event.relatedTarget)) return
  setTyping(false)
}
</script>
